// Advanced Media Type Detection and Helper Functions
// AI-assisted file type detection with enhanced capabilities

// Enhanced media type detection with AI-like intelligence
export const getMediaType = (mimeType, filename = '') => {
  const lowerFilename = filename.toLowerCase();
  
  // Voice note detection (WhatsApp-style)
  if (mimeType === 'audio/ogg' || mimeType === 'audio/webm' || 
      lowerFilename.includes('voice') || lowerFilename.includes('recording')) {
    return 'voice';
  }
  
  // Standard media types
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  
  // Document types with enhanced detection
  if (mimeType === 'application/pdf') return 'document';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'document';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'document';
  if (mimeType.startsWith('text/')) return 'document';
  
  return 'document';
};

// AI-assisted file type categorization
export const getFileCategory = (mimeType, filename = '') => {
  const mediaType = getMediaType(mimeType, filename);
  
  switch (mediaType) {
    case 'image':
      return {
        category: 'visual',
        priority: 1,
        previewable: true,
        requiresThumbnail: true
      };
    case 'video':
      return {
        category: 'visual',
        priority: 2,
        previewable: true,
        requiresThumbnail: true
      };
    case 'audio':
      return {
        category: 'audio',
        priority: 3,
        previewable: false,
        requiresThumbnail: false
      };
    case 'voice':
      return {
        category: 'audio',
        priority: 4,
        previewable: false,
        requiresThumbnail: false,
        isVoiceNote: true
      };
    case 'document':
      return {
        category: 'document',
        priority: 5,
        previewable: mimeType === 'application/pdf',
        requiresThumbnail: false
      };
    default:
      return {
        category: 'unknown',
        priority: 6,
        previewable: false,
        requiresThumbnail: false
      };
  }
};

// Get file icon based on MIME type
export const getFileIcon = (mimeType) => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.startsWith('video/')) return '🎬';
  
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('excel')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
  if (mimeType.startsWith('text/')) return '📄';
  
  return '📎';
};

// Format file size to human readable format
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format duration for audio/video
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Get file extension from filename
export const getFileExtension = (filename) => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

// Check if file is supported
export const isFileSupported = (file) => {
  const supportedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/webm',
    'video/mp4',
    'video/webm',
    'video/ogg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain'
  ];
  
  return supportedTypes.includes(file.type);
};

// Generate thumbnail URL for video (placeholder - in real app, generate actual thumbnails)
export const getVideoThumbnail = (videoUrl) => {
  // In a real implementation, you'd generate thumbnails on the backend
  // For now, return a placeholder or the video URL itself
  return videoUrl;
};

// Download file helper
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Create object URL for preview
export const createPreviewUrl = (file) => {
  return URL.createObjectURL(file);
};

// Revoke object URL to prevent memory leaks
export const revokePreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

// Validate file before upload
export const validateFile = (file) => {
  const errors = [];
  
  // Check file size (25MB max - standardized across all components)
  const maxSize = 25 * 1024 * 1024; // 25MB
  if (file.size > maxSize) {
    errors.push(`File size too large. Maximum size is ${formatFileSize(maxSize)}.`);
  }
  
  // Check file type
  if (!isFileSupported(file)) {
    errors.push('File type not supported. Please upload PDF, image, audio, video, or document files.');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get appropriate aspect ratio for media preview
export const getAspectRatio = (mediaType) => {
  switch (mediaType) {
    case 'image':
      return '16/9';
    case 'video':
      return '16/9';
    case 'audio':
      return '1/1';
    default:
      return '1/1';
  }
};

// Get color for file type
export const getFileTypeColor = (mimeType) => {
  if (mimeType.startsWith('image/')) return '#4CAF50';
  if (mimeType.startsWith('audio/')) return '#2196F3';
  if (mimeType.startsWith('video/')) return '#FF5722';
  if (mimeType === 'application/pdf') return '#F44336';
  if (mimeType.includes('word')) return '#1976D2';
  if (mimeType.includes('excel')) return '#388E3C';
  if (mimeType.includes('powerpoint')) return '#D32F2F';
  return '#757575';
};

// Waveform generation utilities for audio visualization
export const generateWaveformData = (audioBuffer, bars = 50) => {
  const channelData = audioBuffer.getChannelData(0);
  const blockSize = Math.floor(channelData.length / bars);
  const waveform = [];
  
  for (let i = 0; i < bars; i++) {
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(channelData[(i * blockSize) + j]);
    }
    const average = sum / blockSize;
    waveform.push(average);
  }
  
  return waveform;
};

// Generate mock waveform data for demo purposes
export const generateMockWaveform = (duration = 10, bars = 50) => {
  const waveform = [];
  for (let i = 0; i < bars; i++) {
    // Create a natural-looking waveform with some variation
    const baseHeight = 0.2 + Math.random() * 0.3;
    const variation = Math.sin(i * 0.3) * 0.2;
    const height = Math.max(0.1, Math.min(0.9, baseHeight + variation));
    waveform.push(height);
  }
  return waveform;
};

// Calculate waveform bar heights based on current time
export const getWaveformHeights = (waveform, currentTime, duration) => {
  if (!waveform || !duration) return waveform;
  
  const progress = currentTime / duration;
  const activeIndex = Math.floor(progress * waveform.length);
  
  return waveform.map((height, index) => ({
    height,
    isActive: index <= activeIndex
  }));
};

// Smart file type detection with fallback
export const smartDetectFileType = (file) => {
  // First try MIME type
  if (file.type) {
    const mediaType = getMediaType(file.type, file.name);
    if (mediaType !== 'document') return mediaType;
  }
  
  // Fallback to file extension detection
  const ext = getFileExtension(file.name);
  const extensionMap = {
    // Images
    'jpg': 'image', 'jpeg': 'image', 'png': 'image', 'gif': 'image', 'webp': 'image',
    // Videos (webm/ogg are ambiguous containers — MIME type above decides;
    // this extension fallback treats webm as video and ogg as audio)
    'mp4': 'video', 'webm': 'video', 'mov': 'video',
    // Audio
    'mp3': 'audio', 'wav': 'audio', 'ogg': 'audio',
    // Voice notes
    'm4a': 'voice', 'opus': 'voice',
    // Documents
    'pdf': 'document', 'doc': 'document', 'docx': 'document', 'txt': 'document'
  };
  
  return extensionMap[ext] || 'document';
};

// Get appropriate preview dimensions for media type
export const getPreviewDimensions = (mediaType) => {
  switch (mediaType) {
    case 'image':
      return { width: 300, height: 200 };
    case 'video':
      return { width: 320, height: 180 };
    case 'audio':
      return { width: 280, height: 60 };
    case 'voice':
      return { width: 280, height: 80 };
    case 'document':
      return { width: 300, height: 80 };
    default:
      return { width: 200, height: 100 };
  }
};

// Enhanced file validation with detailed feedback
export const enhancedValidateFile = (file) => {
  const result = validateFile(file);
  
  if (!result.isValid) {
    return result;
  }
  
  // Additional validation for specific file types
  const fileType = smartDetectFileType(file);
  const warnings = [];
  
  if (fileType === 'image' && file.size > 10 * 1024 * 1024) {
    warnings.push('Large image file. Consider compressing for faster loading.');
  }
  
  if (fileType === 'video' && file.size > 20 * 1024 * 1024) {
    warnings.push('Large video file. Consider compressing for better performance.');
  }
  
  if (fileType === 'audio' && file.size > 5 * 1024 * 1024) {
    warnings.push('Large audio file. Consider compressing for faster upload.');
  }
  
  return {
    ...result,
    warnings,
    detectedType: fileType,
    category: getFileCategory(file.type || '', file.name)
  };
};

// File compression utilities
export const compressImage = (file, quality = 0.8, maxWidth = 1920) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file); // Return original file if not an image
      return;
    }
    
    const reader = new FileReader();
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    reader.onload = (e) => {
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image with new dimensions
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            
            console.log(`Image compressed: ${formatFileSize(file.size)} → ${formatFileSize(compressedFile.size)}`);
            resolve(compressedFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file for compression'));
    };
    
    reader.readAsDataURL(file);
  });
};

// Compress video (placeholder - in real app, use proper video compression libraries)
export const compressVideo = async (file) => {
  // Note: Real video compression requires more complex libraries
  // For now, return the original file with a warning
  console.warn('Video compression not implemented. Consider using libraries like ffmpeg.js');
  return file;
};

// Compress audio (placeholder - in real app, use proper audio compression)
export const compressAudio = async (file) => {
  // Note: Real audio compression requires more complex libraries
  // For now, return the original file with a warning
  console.warn('Audio compression not implemented. Consider using Web Audio API');
  return file;
};

// Smart compression based on file type
export const smartCompressFile = async (file, options = {}) => {
  const {
    imageQuality = 0.8,
    imageMaxWidth = 1920,
    enableImageCompression = true,
    enableVideoCompression = false,
    enableAudioCompression = false
  } = options;
  
  const fileType = smartDetectFileType(file);
  
  try {
    switch (fileType) {
      case 'image':
        if (enableImageCompression && file.size > 5 * 1024 * 1024) {
          return await compressImage(file, imageQuality, imageMaxWidth);
        }
        break;
        
      case 'video':
        if (enableVideoCompression && file.size > 10 * 1024 * 1024) {
          return await compressVideo(file);
        }
        break;
        
      case 'audio':
        if (enableAudioCompression && file.size > 2 * 1024 * 1024) {
          return await compressAudio(file);
        }
        break;
        
      default:
        // No compression for documents and other file types
        break;
    }
    
    return file; // Return original file if no compression applied
  } catch (error) {
    console.warn('File compression failed, using original file:', error.message);
    return file; // Return original file on compression failure
  }
};

// Batch compression for multiple files
export const compressFiles = async (files, options = {}) => {
  const compressedFiles = [];
  
  for (const file of files) {
    try {
      const compressedFile = await smartCompressFile(file, options);
      compressedFiles.push(compressedFile);
    } catch (error) {
      console.warn(`Failed to compress ${file.name}:`, error.message);
      compressedFiles.push(file); // Use original file on error
    }
  }
  
  return compressedFiles;
};
